import { useMemo, useRef } from 'react';
import { zeroPad } from 'react-countdown';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ComponentGuard from '@/components/ui/custom/ComponentGuard';
import ConditionalWrapper from '@/components/ui/custom/ConditionalWrapper';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import TextAreaInput from '@/components/ui/custom/dataInput/TextAreaInput';
import { ModularCard } from '@/components/ui/custom/modular/ModularCard';
import TagInput from '@/components/tags/TagInput';
import useToggle from '@/data/hook/useToggle';
import { StudioPermissions } from '@/enums/VersioningStatus';
import { LessonMatrixGenerate } from '@/models/astro.ai/generate/LessonMatrix';
import { TempLesson } from '@/models/Lesson';
import { UserTypeEnum } from '@/models/User';
import { GenerateLessonDetail } from '../actions/GenerateLessonDetail';
import { CourseStudioFormFields } from '../CourseStudio.hooks';
import LessonBookChapters from './LessonBookChapters';

type Props = {
  blockIndex: number;
  lessonIndex: number;
  lesson: TempLesson;
  globalLessonIndex: number;
  studioPermissions: StudioPermissions;
  coursePathId?: number;
  loading?: boolean;
};

export default function LessonDetail(props: Props) {
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const { t } = useTranslation('translation', {
    keyPrefix: 'courseStudio',
  });

  const {
    globalLessonIndex,
    blockIndex,
    lessonIndex,
    studioPermissions,
    coursePathId,
    loading,
  } = props;

  const { setValue, watch } = useFormContext<CourseStudioFormFields>();

  const startOpen = blockIndex === 0 && lessonIndex === 0;

  const { isOpen, toggle, open } = useToggle(startOpen);

  const startAllChaptersOpen = useRef(false);

  const lessonName = useMemo(() => {
    const formattedLessonName = `${tCommon('lesson', {
      order: zeroPad(globalLessonIndex + 1, 2),
    })} - ${props.lesson.name}`;

    return formattedLessonName;
  }, [globalLessonIndex, tCommon, props.lesson.name]);

  const onGenerateLessonDetail = (result: LessonMatrixGenerate) => {
    startAllChaptersOpen.current = true;
    setValue(
      `lessonBlocks.${blockIndex}.lessons.${lessonIndex}.chapters`,
      result.chapters,
    );
    setValue(
      `lessonBlocks.${blockIndex}.lessons.${lessonIndex}.tagsIds`,
      result.contentTags,
    );

    setValue(
      `lessonBlocks.${blockIndex}.lessons.${lessonIndex}.description`,
      result.objectives,
    );
    open();
  };

  const disabled = useMemo(
    () =>
      !studioPermissions.allowOtherSteps.simpleEditing &&
      !studioPermissions.allowOtherSteps.advancedEditing,
    [studioPermissions],
  );

  const onToggle = () => {
    startAllChaptersOpen.current = false;
    toggle();
  };

  return (
    <li>
      <ModularCard>
        <ModularCard.Header
          title={lessonName}
          onOpen={onToggle}
          isOpen={isOpen}
          className={{
            title: 'font-400',
            section: 'grow max-w-[80%]',
            button: 'relative right-0',
            base: 'flex-wrap',
          }}
        >
          <GenerateLessonDetail
            coursePathId={coursePathId}
            lessonOrder={globalLessonIndex + 1}
            lessonName={props.lesson.name}
            disabled={disabled || loading}
            locked={!coursePathId}
            onGenerate={onGenerateLessonDetail}
          />
        </ModularCard.Header>

        <ModularCard.Accordion isOpen={isOpen} className={{ base: 'px-4' }}>
          <fieldset className="flex flex-col gap-2.5 pb-3.5">
            <LessonDescription
              blockIndex={blockIndex}
              lessonIndex={lessonIndex}
              studioPermissions={studioPermissions}
            />
            <LessonBookChapters
              blockIndex={blockIndex}
              lessonIndex={lessonIndex}
              studioPermissions={studioPermissions}
              startAllOpen={startAllChaptersOpen.current}
            />
            <ComponentGuard roles={[UserTypeEnum.SUPER_ADMIN]}>
              <ConditionalWrapper
                condition={disabled}
                wrapper={
                  <Tooltip
                    text={t('notAllowedIfNotAtLeastInLimitedEdition')}
                    classNameContainer="flex w-full"
                  />
                }
              >
                <TagInput
                  className={{ base: 'w-full' }}
                  disabled={disabled}
                  value={watch(
                    `lessonBlocks.${blockIndex}.lessons.${lessonIndex}.tagsIds`,
                  )}
                  onChange={tagsIds =>
                    setValue(
                      `lessonBlocks.${blockIndex}.lessons.${lessonIndex}.tagsIds`,
                      tagsIds,
                    )
                  }
                />
              </ConditionalWrapper>
            </ComponentGuard>
          </fieldset>
        </ModularCard.Accordion>
      </ModularCard>
    </li>
  );
}

type LessonDescriptionProps = {
  blockIndex: number;
  lessonIndex: number;
  studioPermissions: StudioPermissions;
};

function LessonDescription(props: LessonDescriptionProps) {
  const { blockIndex, lessonIndex, studioPermissions } = props;

  const {
    control,
    formState: { errors },
  } = useFormContext<CourseStudioFormFields>();

  const { t } = useTranslation('translation', {
    keyPrefix: 'courseStudio',
  });

  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const disabled = useMemo(
    () =>
      !studioPermissions.allowOtherSteps.simpleEditing &&
      !studioPermissions.allowOtherSteps.advancedEditing,
    [studioPermissions],
  );

  return (
    <Controller
      control={control}
      name={`lessonBlocks.${blockIndex}.lessons.${lessonIndex}.description`}
      render={({ field: { onChange, value } }) => (
        <ConditionalWrapper
          condition={disabled}
          wrapper={
            <Tooltip
              text={t('notAllowedIfNotAtLeastInLimitedEdition')}
              classNameContainer="flex w-full"
            />
          }
        >
          <TextAreaInput
            disabled={disabled}
            value={value || ''}
            onChange={onChange}
            name={`lessonBlocks.${blockIndex}.lessons.${lessonIndex}.description`}
            label={tCommon('objectives')}
            className={{
              base: 'w-full',
              label: 'font-500',
              input: 'border-base-content/25 font-400',
            }}
            placeholder={t('endLessonTheStudentShould')}
            errorLabelText={
              errors.lessonBlocks?.[blockIndex]?.lessons?.[lessonIndex]
                ?.description?.message
            }
          />
        </ConditionalWrapper>
      )}
    />
  );
}
